// 📄 components/Configuracion/EditarPlantillaProyectoModal.tsx
// Modal de edición de una plantilla propia del proyecto (ver
// ConfiguracionPlantillasXml.tsx) — antes esto era un rename inline en la
// misma fila de la lista, sin forma de reemplazar el archivo XML ni de
// configurar restricciones. Mismo patrón que CargarPlantillaModal.tsx
// (alta) y PlantillaGlobalFormModal.tsx (edición global, Admin): reutiliza
// XmlDropzone.tsx y RestriccionesTagEditor.tsx, no duplica ninguna lógica.
//
// El listado de plantillas del proyecto (PlantillaProyecto) no trae
// contenidoXML ni restricciones — a propósito, la lista solo pide metadata
// (ver comentario en plantillaReferenciaService.ts) — así que al abrir este
// modal se pide el detalle completo vía resolverPlantilla (mismo alcance
// "proyecto" que ya resuelve con precedencia), necesario para extraer los
// tags configurables y mostrar las restricciones ya guardadas.
import { useEffect, useState } from "react";
import { X, Save, RefreshCw } from "lucide-react";
import XmlDropzone from "../shared/XmlDropzone.tsx";
import RestriccionesTagEditor from "../shared/RestriccionesTagEditor.tsx";
import PlantillaMetadatosForm from "../shared/PlantillaMetadatosForm.tsx";
import {
  guardarPlantilla, guardarRestricciones, renombrarPlantillaProyecto, resolverPlantilla, PlantillaProyecto, PlantillaMetadatos
} from "../../services/plantillaReferenciaService.ts";
import { RestriccionTag } from "../../utils/xmlStructureUtils.ts";

interface EditarPlantillaProyectoModalProps {
  projectId: string;
  plantilla: PlantillaProyecto;
  onClose: () => void;
  onGuardado: () => void;
}

export default function EditarPlantillaProyectoModal({ projectId, plantilla, onClose, onGuardado }: EditarPlantillaProyectoModalProps) {
  const [nombre, setNombre] = useState(plantilla.nombre);
  // Archivo NUEVO elegido en el dropzone (vacío mientras no se reemplace).
  const [nombreArchivoNuevo, setNombreArchivoNuevo] = useState("");
  const [contenidoXMLNuevo, setContenidoXMLNuevo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Detalle completo (contenidoXML actual + restricciones ya guardadas),
  // pedido al abrir el modal — ver comentario de arriba.
  const [cargandoDetalle, setCargandoDetalle] = useState(true);
  const [contenidoXMLActual, setContenidoXMLActual] = useState("");
  const [restricciones, setRestricciones] = useState<RestriccionTag[]>([]);
  const [restriccionesOriginales, setRestriccionesOriginales] = useState<RestriccionTag[]>([]);
  const [metadatos, setMetadatos] = useState<PlantillaMetadatos>({});
  const [metadatosOriginales, setMetadatosOriginales] = useState<PlantillaMetadatos>({});
  const [detalleDisponible, setDetalleDisponible] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCargandoDetalle(true);
    resolverPlantilla(plantilla.tipoDocumento, projectId)
      .then((resuelta) => {
        if (cancelado) return;
        if (resuelta.encontrada && resuelta.plantilla) {
          setContenidoXMLActual(resuelta.plantilla.contenidoXML || "");
          const actuales = resuelta.plantilla.restricciones || [];
          setRestricciones(actuales);
          setRestriccionesOriginales(actuales);
          const metadatosActuales: PlantillaMetadatos = {
            documentType: resuelta.plantilla.documentType || "",
            documentStage: resuelta.plantilla.documentStage || "",
            documentStandard: resuelta.plantilla.documentStandard || "",
            sistemaOrigen: resuelta.plantilla.sistemaOrigen || "",
            sistemaDestino: resuelta.plantilla.sistemaDestino || "",
            descripcion: resuelta.plantilla.descripcion || ""
          };
          setMetadatos(metadatosActuales);
          setMetadatosOriginales(metadatosActuales);
          setDetalleDisponible(true);
        } else {
          setError("No se pudo cargar el detalle de esta plantilla — el archivo puede haberse eliminado.");
        }
      })
      .catch((err) => {
        if (cancelado) return;
        console.error("Error cargando el detalle de la plantilla:", err);
        setError("No se pudo cargar el detalle de esta plantilla. Podés seguir renombrándola, pero no configurar restricciones por ahora.");
      })
      .finally(() => { if (!cancelado) setCargandoDetalle(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantilla.tipoDocumento, projectId]);

  // Igual criterio que PlantillaGlobalFormModal.tsx: si se reemplaza el
  // archivo O cambiaron los metadatos, guardarPlantilla (.set()) sobrescribe
  // el documento entero, así que necesita el snapshot vigente de
  // restricciones (y del XML actual si no se subió uno nuevo). Si nada de
  // eso cambió, nombre y restricciones se guardan cada uno con su propio
  // partial update (renombrarPlantillaProyecto / guardarRestricciones), sin
  // tocar el archivo — y sin arriesgar pisar restricciones con un array
  // vacío si el detalle nunca llegó a cargar (detalleDisponible en false).
  const handleGuardar = async () => {
    setError(null);
    if (!nombre.trim()) { setError("Escribe un nombre para el tipo de documento."); return; }

    setGuardando(true);
    try {
      const metadatosCambiaron = JSON.stringify(metadatos) !== JSON.stringify(metadatosOriginales);
      if (contenidoXMLNuevo) {
        await guardarPlantilla(plantilla.tipoDocumento, nombreArchivoNuevo, contenidoXMLNuevo, "proyecto", projectId, nombre.trim(), restricciones, metadatos);
      } else if (metadatosCambiaron) {
        await guardarPlantilla(plantilla.tipoDocumento, plantilla.nombreArchivo, contenidoXMLActual, "proyecto", projectId, nombre.trim(), restricciones, metadatos);
      } else {
        const tareas: Promise<void>[] = [];
        if (nombre.trim() !== plantilla.nombre) {
          tareas.push(renombrarPlantillaProyecto(projectId, plantilla.tipoDocumento, nombre.trim()));
        }
        if (detalleDisponible && JSON.stringify(restricciones) !== JSON.stringify(restriccionesOriginales)) {
          tareas.push(guardarRestricciones(plantilla.tipoDocumento, restricciones, "proyecto", projectId));
        }
        await Promise.all(tareas);
      }
      onGuardado();
    } catch (err: any) {
      setError(err.message || "Error al guardar los cambios.");
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-xl p-3 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  // El XML "vigente" para extraer tags: el nuevo si se eligió uno, si no el
  // actual ya guardado (recién llegado del fetch).
  const contenidoXMLParaTags = contenidoXMLNuevo || contenidoXMLActual;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      {/* max-h-[90vh] + flex-col: la sección de restricciones puede crecer
          bastante, header y footer siempre quedan visibles (ver mismo
          patrón en PlantillaGlobalFormModal.tsx). */}
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">Editar plantilla</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Nombre del tipo de documento</label>
            <input
              type="text"
              autoFocus
              className={inputClass}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="ej. Aviso de Recepción"
            />
          </div>

          <div>
            <label className={labelClass}>Archivo XML</label>
            <XmlDropzone
              accent="indigo"
              nombreArchivo={nombreArchivoNuevo}
              contenidoArchivo={contenidoXMLNuevo}
              onArchivoCargado={(n, c) => { setNombreArchivoNuevo(n); setContenidoXMLNuevo(c); }}
              onError={(msg) => setError(msg)}
            />
            <p className="text-xs text-gray-400 mt-2">
              Archivo actual: <span className="text-gray-200">{plantilla.nombreArchivo || "Sin archivo"}</span>
            </p>
            {contenidoXMLNuevo && (
              <p className="text-[11px] text-indigo-400 mt-1">Se reemplazará al guardar.</p>
            )}
          </div>

          <div className="pt-2 border-t border-white/10">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Metadatos de trazabilidad (opcional)</h4>
            {cargandoDetalle ? (
              <p className="text-xs text-gray-500 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando metadatos guardados...</p>
            ) : (
              <PlantillaMetadatosForm valores={metadatos} onChange={setMetadatos} />
            )}
          </div>

          <div className="pt-2 border-t border-white/10">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Restricciones por campo (opcional)</h4>
            {cargandoDetalle ? (
              <p className="text-xs text-gray-500 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando restricciones configuradas...</p>
            ) : !detalleDisponible && !contenidoXMLNuevo ? (
              <p className="text-xs text-gray-500">No se pudo cargar el archivo actual para configurar restricciones. Subí un archivo nuevo arriba si querés reemplazarlo, o cerrá y volvé a intentar.</p>
            ) : (
              <RestriccionesTagEditor contenidoXML={contenidoXMLParaTags} restricciones={restricciones} onChange={setRestricciones} />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10 flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all">Cancelar</button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando || !nombre.trim()}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
          >
            {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {guardando ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
